import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { getCompanyByUserID } from '../../../api/company.js';
import { AdminCompanyLayout } from '../../components/admin-company-layout.js';
import { companyRouter } from './company-routes.js';

export const MwCompanyDashboard = stateful(() => {
  const userObs = currentUser<User>();
  const user = useQuery(() => userObs.result, [userObs]);
  // re-render on language change (subscription side-effect)
  useQuery(() => globalLanguageSubject.asObservable(), []);
  const company = useQuery(async () => {
    if (!user.value?._id) return null;
    return await getCompanyByUserID(user.value._id)({
      _id: 1,
      name: 1,
      logoID: 1,
      isActive: 1,
      cultureAndValues: { langKey: 1 },
      benefits: { langKey: 1 },
    });
  }, [user.value?._id]);

  // Show welcome banner when company profile is incomplete
  const isProfileIncomplete =
    company.value &&
    !company.value.cultureAndValues?.length &&
    !company.value.benefits?.length &&
    !company.value.logoID;

  const activeRouteName = companyRouter.currentRoute.value.route.name;

  const sidebarContent = html`
    <li class="menu-title"><span>${m.company_dashboard_menu_title()}</span></li>
    <li>
      <a
        class="gap-3 rounded-lg hover:bg-base-300/60 ${activeRouteName === 'companyProfile'
          ? 'active bg-base-300/80'
          : ''}"
        @click=${() => companyRouter.goTo('companyProfile')}
      >
        <span class="icon-[material-symbols--badge]"></span>
        ${m.menuCompanyProfile()}
      </a>
    </li>
    <li>
      <a
        class="gap-3 rounded-lg hover:bg-base-300/60 ${activeRouteName === 'root' ? 'active bg-base-300/80' : ''}"
        @click=${() => companyRouter.goTo('root')}
      >
        <span class="icon-[material-symbols--work]"></span>
        ${m.menuJobPosting()}
      </a>
    </li>
  `;

  return AdminCompanyLayout({
    sidebarContent,
    pageTitle: html`
      <div class="flex-1 px-2 mx-2">
        <div class="flex items-center gap-3">
          <div class="flex flex-col">
            <div class="font-bold leading-tight">${company.value?.name ?? m.company()}</div>
            <div class="text-xs text-base-content/60">${m.company_dashboard_brand()}</div>
          </div>
        </div>
      </div>
    `,
    content: html`
      ${company.loading
        ? html`<div class="flex justify-center py-20"><span class="loading loading-spinner loading-lg"></span></div>`
        : !company.value
          ? html`
              <div class="hero min-h-[60vh] bg-base-200 rounded-box">
                <div class="hero-content text-center">
                  <div class="max-w-md">
                    <div class="mb-3 flex justify-center">
                      <div
                        class="bg-base-300 text-base-content w-16 h-16 rounded-full flex items-center justify-center"
                      >
                        <span class="icon-[material-symbols--apartment] text-3xl"></span>
                      </div>
                    </div>
                    <h2 class="text-2xl font-bold mb-2">${m.company_no_company_assigned_title()}</h2>
                    <p class="opacity-70">${m.company_no_company_assigned_text()}</p>
                  </div>
                </div>
              </div>
            `
          : html`
              ${company.value.isActive === false
                ? html`
                    <div class="alert alert-warning mb-6">
                      <span class="icon-[material-symbols--archive]"></span>
                      <span>${m.company_dashboard_archived_banner()}</span>
                    </div>
                  `
                : nothing}
              ${isProfileIncomplete
                ? html`
                    <div class="alert bg-warning/20 border-warning mb-6">
                      <div class="flex flex-col gap-3 w-full items-center text-center">
                        <p>${m.company_welcome_banner_text()}</p>
                        <button
                          class="btn btn-primary btn-wide"
                          @click=${() => companyRouter.goTo('companyProfile')}
                        >
                          ${m.company_welcome_banner_cta()}
                        </button>
                      </div>
                    </div>
                  `
                : nothing}
              ${companyRouter.outlet.value({ companyID: company.value._id })}
            `}
    `,
    company: company.value,
  });
});
